import React from "react";
import cn from "classnames";
import { SquareButton, Text } from "@themuse/design-system";
import { GroupListFieldType, GroupListItem } from "@themuse/design-system/lib/components/GroupList/GroupList";
import { CloseIcon } from "@themuse/design-system/lib/components/Icons";
import { useSearchResultsState } from "hooks/SearchResultsContext";
import {
    FiltersActionTypes,
    UserInteractionActionTypes,
    LocationActionTypes
} from "interfaces/context/searchContextInterfaces";
import { useSearchState, useSearchDispatch } from "hooks/SearchContext";
import { isMobile, toggleWindowFreeze } from "utils/browser";
import { formatNumberComma } from "utils/strings";
import { getSelectedFiltersCount } from "utils/searchFacets";

import { trackFacets } from "utils/searchFacetsTracking";
import styles from "./SearchFacets.module.scss";
import JobFacets from "./jobs/JobFacets";
import CompanyFacets from "./companies/CompanyFacets";

interface SearchFacetsProps {
    onClose?: () => void;
    onFiltersChange?: () => void;
}

type CheckboxFilterName =
    // Company filter names
    "companyLeadership"
    | "diversityAndInclusion"
    | "companySize"
    | "industry"
    | "perks"
    // Job filter names
    | "experienceLevel"
    | "category"
    | "radius"
    | "postedDateRange";

export interface SearchFilterItemProps {
    /**
     * Filter property name defined in SearchFilters interface.
     */
    userInputFilterName: CheckboxFilterName;

    /**
     * Array of objects used to populate the search facet.
     */
    searchFilterItems: GroupListItem[];

    /**
     * Label of the filter button and filter container.
     */
    labelText: string;

    /**
     * The type of filter field (radio or checkbox).
     */
    fieldType: GroupListFieldType;

    /**
     * Default value for radio options
     */
    defaultValue?: string;

    /**
     * Should filter be disabled when company is selected?
     */
    disableOnCompanySelect?: boolean;
}

const SearchFacets = (props: SearchFacetsProps) => {
    const { onClose, onFiltersChange } = props;
    const {
        company, location, filters, userInteractions
    } = useSearchState();
    const searchDispatch = useSearchDispatch();
    const [selectedFiltersState, setselectedFiltersState] = React.useState(filters);
    const searchResultsState = useSearchResultsState();
    const isRemoteType = location?.userSelectedLocation?.remote_work_location_type === "virtual";

    React.useEffect(() => {
        let filterSource = "individual_filter";
        if (userInteractions?.mobileClearAllClickedAt
            || userInteractions?.desktopClearAllClickedAt) {
            filterSource = "clear_all_filters_button";
        } else if (userInteractions?.individualFilterClearedAt) {
            filterSource = "clear_individual_filter_button";
        }

        // Filter was applied if values in filters does not exist in selectedFiltersState
        trackFacets("Applied Filter", filterSource, filters, selectedFiltersState);

        // Filter was removed if values in selectedFiltersState does not exist in filters
        trackFacets("Removed Filter", filterSource, selectedFiltersState, filters);

        setselectedFiltersState(filters);
    }, [filters]);

    const clearFilters = () => {
        searchDispatch({
            type: UserInteractionActionTypes.updateFilterInteraction,
            payload: {
                [isMobile() ? "mobileClearAllClickedAt" : "desktopClearAllClickedAt"]: Date.now()
            }
        });
        searchDispatch({
            type: FiltersActionTypes.resetAllFilters,
        });

        // Reset the remote checkbox is remote is selected
        if (isRemoteType) {
            searchDispatch({
                type: LocationActionTypes.resetLocation,
            });
        }
        onFiltersChange?.();
    };

    const filterCount = getSelectedFiltersCount(
        filters,
        location?.userSelectedLocation?.remote_work_location_type
    );

    const clearMessage = `Clear ${filterCount} filter${filterCount > 1 ? "s" : ""}`;

    const viewResultsCount = searchResultsState.totalCount >= 10000 ? "10,000+"
        : formatNumberComma(searchResultsState.totalCount);
    const viewResultsMessage = `View ${viewResultsCount} Result${searchResultsState.totalCount > 1 ? "s" : ""}`;

    const viewResultsOnClick = () => {
        onClose();
        if (isMobile()) {
            toggleWindowFreeze();
        }
    };

    const dispatchUpdateRemoteCheckbox = (remoteIsSelected: boolean) => {
        let payload = {
            userInputValue: "",
            userSelectedLocation: null
        };

        if (!remoteIsSelected) {
            payload = {
                userInputValue: "Remote / Flexible",
                userSelectedLocation: {
                    label: "Remote / Flexible",
                    value: "Remote / Flexible",
                    latitude: null,
                    longitude: null,
                    remote_work_location_type: "virtual"
                }
            };
        }

        searchDispatch({
            type: LocationActionTypes.updateLocation,
            payload
        });
        if (filters?.radius) {
            searchDispatch({
                type: FiltersActionTypes.updateFilter,
                payload: {
                    radius: ""
                }
            });
        }
    };

    const locationIsSelected = (!!location?.userSelectedLocation?.value
                                && !!location?.userSelectedLocation?.latitude
                                && !!location?.userSelectedLocation?.longitude
                                && !isRemoteType);

    return (
        <div className={styles.container}>
            <header className={cn(styles.mainHeader, styles.header)}>
                <Text textStyle="heading-3" className={styles.headerText}>All Filters</Text>
                { filterCount > 0 ? (
                    <button
                        type="button"
                        className={styles.headerButton}
                        onClick={clearFilters}
                    >
                        Clear All
                    </button>
                ) : null}
            </header>
            <JobFacets
                filters={filters}
                company={company}
                dispatch={searchDispatch}
                remoteIsSelected={isRemoteType}
                updateRemoteIsSelected={dispatchUpdateRemoteCheckbox}
                locationIsSelected={locationIsSelected}
            />
            <CompanyFacets
                filters={filters}
                company={company}
                dispatch={searchDispatch}
            />
            { filterCount > 0 ? (
                <button
                    type="button"
                    className={styles.clearFacets}
                    onClick={clearFilters}
                >
                    <CloseIcon className={styles.clearFacetsIcon} />
                    <Text
                        textStyle="sans-serif-body-small"
                        htmlTag="span"
                        className={styles.clearFacetsText}
                    >
                        {clearMessage}
                    </Text>
                </button>
            ) : null}
            <footer className={styles.footer}>
                <SquareButton
                    className={styles.viewResults}
                    color="secondary-100"
                    onClick={viewResultsOnClick}
                >
                    {viewResultsMessage}
                </SquareButton>
            </footer>
        </div>
    );
};

export default SearchFacets;
